import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import ConfirmModal from '../../components/ConfirmModal';
import AdminQuestionModal from '../../components/admin/AdminQuestionModal';
import MarkdownEditor from '../../components/admin/MarkdownEditor';
import { useConfirm } from '../../components/admin/useConfirm';
import { SUBJECTS } from './adminConstants';

type Line = { id?: string; _k?: string; type: number; name: string; url: string; order: number };
type QRef = { id: string; text: string; type: number; topic: string; difficulty: number; options: number };
type Lesson = { id?: string; number: number; title: string; caption: string; studyText: string; difficulty: number; subject: number; published: boolean; lines: Line[]; questions: QRef[] };

// Note: line type 2 ("Test") is intentionally omitted — the learner LessonPage never
// renders it; lesson quizzes come from the linked question list ("Kvíz lekce"), not a line.
const LINETYPES = [{ v: 0, l: "Dokument" }, { v: 1, l: "Video" }, { v: 3, l: "Bonus" }, { v: 4, l: "Odkaz" }];

// Mirrors AdminContentService.AllowedExtensions / RequestSizeLimit on the backend.
const UPLOAD_ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.svg,.mp4,.webm,.mov,.m4v,.mp3,.doc,.docx,.ppt,.pptx,.xls,.xlsx";
const MAX_UPLOAD_MB = 500;
const VIDEO_EXT = /\.(mp4|webm|mov|m4v)$/i;
const isUploadedFile = (url: string) => url.startsWith("/uploads");
// Pick Dokument/Video from the actual file, but keep an intentional Bonus/Odkaz choice.
const inferLineType = (current: number, fileName: string) =>
    current === 3 || current === 4 ? current : VIDEO_EXT.test(fileName) ? 1 : 0;

let lineSeq = 0;
const nextLineKey = () => "ln" + (++lineSeq);

export default function AdminLessonEditor() {
    const { id } = useParams();
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const api = new ApiHelper();
    const isNew = id === "new";

    const [l, setL] = useState<Lesson | null>(null);
    const [confirmDel, setConfirmDel] = useState(false);
    const [qModal, setQModal] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
    const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
    const { ask, confirmModal } = useConfirm();

    useEffect(() => { load(); }, [id]);

    async function load() {
        if (isNew) {
            setL({ number: 1, title: "", caption: "", studyText: "", difficulty: 1, subject: Number(sp.get("subject") ?? 0), published: false, lines: [], questions: [] });
            return;
        }
        try { setL(await (await api.adminContentApi.lesson(id!)).json()); } catch (x: any) { api.adminError(x); }
    }

    function setLine(i: number, patch: Partial<Line>) { if (l) setL({ ...l, lines: l.lines.map((x, idx) => idx === i ? { ...x, ...patch } : x) }); }
    function addLine() { if (l) setL({ ...l, lines: [...l.lines, { type: 1, name: "", url: "", order: l.lines.length, _k: nextLineKey() }] }); }
    function removeLine(i: number) { if (l) setL({ ...l, lines: l.lines.filter((_, idx) => idx !== i).map((x, idx) => ({ ...x, order: idx })) }); }
    function moveLine(i: number, dir: number) { if (!l) return; const j = i + dir; if (j < 0 || j >= l.lines.length) return; const lines = [...l.lines]; [lines[i], lines[j]] = [lines[j], lines[i]]; setL({ ...l, lines: lines.map((x, idx) => ({ ...x, order: idx })) }); }

    async function uploadLine(i: number, file: File) {
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            api.alert.error(`Soubor je příliš velký (max ${MAX_UPLOAD_MB} MB)`);
            return;
        }
        setUploadingIdx(i);
        try {
            const res = await (await api.adminContentApi.upload(file)).json();
            const cur = l?.lines[i];
            setLine(i, { url: res.url, name: cur?.name?.trim() ? cur.name : file.name, type: inferLineType(cur?.type ?? 0, file.name) });
            api.alert.success("Soubor nahrán");
        } catch (x: any) { api.adminError(x); }
        finally { setUploadingIdx(null); }
    }

    async function save() {
        if (!l) return;
        const payload = { number: l.number, title: l.title, caption: l.caption, studyText: l.studyText, difficulty: l.difficulty, subject: l.subject, published: l.published, lines: l.lines.map((x, i) => ({ ...x, order: i })) };
        try {
            if (isNew) {
                const res = await (await api.adminContentApi.createLesson(payload)).json();
                api.alert.success("Lekce vytvořena");
                navigate("/admin/vyuka/" + res.id, { replace: true });
            } else {
                await api.adminContentApi.updateLesson(id!, payload);
                api.alert.success("Lekce uložena");
                await load();
            }
        } catch (x: any) { api.adminError(x); }
    }

    async function delLesson() {
        try { await api.adminContentApi.deleteLesson(id!); api.alert.success("Lekce smazána"); navigate("/admin/vyuka"); } catch (x: any) { api.adminError(x); }
    }
    function delQuestion(q: QRef) {
        ask(<>Smazat otázku „{q.text || "(bez znění)"}"?</>, async () => {
            try { await api.adminContentApi.deleteQuestion(q.id); await load(); } catch (x: any) { api.adminError(x); }
        });
    }

    if (!l) return <div><span className="backLink" onClick={() => navigate("/admin/vyuka")}>← Výuka</span></div>;

    return (
        <div>
            <span className="backLink" onClick={() => navigate("/admin/vyuka")}>← Výuka</span>
            <div className="adminHead">
                <div><h1>{isNew ? "Nová lekce" : "Lekce " + l.number + " · " + (l.title || "")}</h1><p>{SUBJECTS.find(s => s.v === l.subject)?.l}</p></div>
                <div className="aActions">
                    {!isNew && <button className="aBtn danger" onClick={() => setConfirmDel(true)}>Smazat</button>}
                    <button className="aBtn" onClick={save}>{isNew ? "Vytvořit" : "Uložit"}</button>
                </div>
            </div>

            <div className="aGrid">
                <div className="aCard">
                    <h2>Základ</h2>
                    <div className="inlineRow">
                        <div className="aField inline"><label>Číslo</label><input type="number" min={1} value={l.number} onChange={e => setL({ ...l, number: Number(e.target.value) })} /></div>
                        <div className="aField inline"><label>Předmět</label><select value={l.subject} onChange={e => setL({ ...l, subject: Number(e.target.value) })}>{SUBJECTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}</select></div>
                        <div className="aField inline"><label>Obtížnost (1–5)</label><input type="number" min={1} max={5} value={l.difficulty} onChange={e => setL({ ...l, difficulty: Number(e.target.value) })} /></div>
                    </div>
                    <div className="aField"><label>Stav</label>
                        <label className="toggle"><input type="checkbox" checked={l.published} onChange={e => setL({ ...l, published: e.target.checked })} /><i className="slider" /> {l.published ? "Publikováno (viditelné studentům)" : "Koncept (skryté)"}</label>
                    </div>
                    <div className="aField"><label>Název</label><input value={l.title} onChange={e => setL({ ...l, title: e.target.value })} /></div>
                    <div className="aField"><label>Podtitulek</label><input value={l.caption} onChange={e => setL({ ...l, caption: e.target.value })} /></div>
                    <div className="aField"><label>Studijní text</label><MarkdownEditor value={l.studyText} onChange={t => setL({ ...l, studyText: t })} height={440} placeholder="Studijní text lekce…" /></div>
                </div>

                <div className="aCard">
                    <h2>Materiály</h2>
                    <div className="rowList">
                        {l.lines.map((ln, i) => {
                            const uploading = uploadingIdx === i;
                            const uploaded = isUploadedFile(ln.url);
                            return (
                            <div key={ln.id ?? ln._k ?? i} className="matRow">
                                <select value={ln.type} disabled={uploading} onChange={e => setLine(i, { type: Number(e.target.value) })}>{LINETYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}</select>
                                <input value={ln.name} onChange={e => setLine(i, { name: e.target.value })} placeholder="Název" />
                                <input value={ln.url} onChange={e => setLine(i, { url: e.target.value })} placeholder="URL nebo nahraj soubor →" />
                                <label className={"up" + (uploaded ? " done" : "") + (uploading ? " busy" : "")} title={uploaded ? "Nahraný soubor — klikni pro nahrazení" : "Nahrát soubor (PDF, obrázek, video…)"}>
                                    {uploading ? "…" : uploaded ? "✓" : "⬆"}
                                    <input type="file" accept={UPLOAD_ACCEPT} disabled={uploading} onChange={e => { const f = e.target.files?.[0]; if (f) uploadLine(i, f); e.currentTarget.value = ""; }} />
                                </label>
                                <span className="ord" onClick={() => moveLine(i, -1)}>↑</span>
                                <span className="ord" onClick={() => moveLine(i, 1)}>↓</span>
                                <span className="x" onClick={() => removeLine(i)}>×</span>
                            </div>
                            );
                        })}
                        {l.lines.length === 0 && <span className="empty">Zatím žádné materiály.</span>}
                    </div>
                    <button className="aBtn ghost" style={{ marginTop: "10px" }} onClick={addLine}>+ Přidat materiál</button>
                </div>
            </div>

            <div className="aCard" style={{ marginTop: "18px" }}>
                <div className="cardHeadRow">
                    <h2>Kvíz lekce</h2>
                    {!isNew && <button className="aBtn ghost" onClick={() => setQModal({ open: true, id: null })}>+ Přidat otázku</button>}
                </div>
                {isNew ? <span className="empty">Otázky lze přidat po vytvoření lekce.</span> :
                    <div className="rowList">
                        {l.questions.map(q => (
                            <div key={q.id} className="lineItem">
                                <span onClick={() => setQModal({ open: true, id: q.id })} style={{ cursor: "pointer", flex: 1 }}><b>{q.text || "(bez znění)"}</b> · {q.topic} · {q.options} možností</span>
                                <span className="x" onClick={() => delQuestion(q)}>×</span>
                            </div>
                        ))}
                        {l.questions.length === 0 && <span className="empty">Žádné otázky.</span>}
                    </div>
                }
            </div>

            {qModal.open && <AdminQuestionModal questionId={qModal.id} subject={l.subject} lessonId={l.id} onClose={() => setQModal({ open: false, id: null })} onSaved={() => { setQModal({ open: false, id: null }); load(); }} />}

            <ConfirmModal open={confirmDel} title="Smazat lekci?" message="Lekce a její materiály budou smazány; navázané otázky se odpojí." confirmLabel="Smazat" cancelLabel="Zrušit" onConfirm={() => { setConfirmDel(false); delLesson(); }} onClose={() => setConfirmDel(false)} />
            {confirmModal}
        </div>
    );
}
